export const SCHEDULE_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const getSchedulePeriods = (daySchedule) => {
  if (
    Array.isArray(daySchedule?.timePeriods) &&
    daySchedule.timePeriods.length > 0
  ) {
    return daySchedule.timePeriods.map((period) => ({
      startTime: period?.startTime || "",
      endTime: period?.endTime || "",
    }));
  }

  if (Array.isArray(daySchedule?.periods) && daySchedule.periods.length > 0) {
    return daySchedule.periods.map((period) => ({
      startTime: period?.startTime || "",
      endTime: period?.endTime || "",
    }));
  }

  if (daySchedule?.startTime || daySchedule?.endTime) {
    return [
      {
        startTime: daySchedule.startTime || "",
        endTime: daySchedule.endTime || "",
      },
    ];
  }

  return [{ startTime: "09:00", endTime: "17:00" }];
};

const toMinutes = (time) => {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time || "")) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const DAY_MINUTES = 24 * 60;

const getPeriodRanges = (period) => {
  const start = toMinutes(period.startTime);
  const end = toMinutes(period.endTime);
  if (start === null || end === null || start === end) return [];
  return end > start
    ? [[start, end]]
    : [[start, DAY_MINUTES], [0, end]];
};

export const getNextSchedulePeriod = (daySchedule) => {
  const periods = getSchedulePeriods(daySchedule);
  const ranges = periods
    .flatMap(getPeriodRanges)
    .filter(([start, end]) => end > start)
    .sort(([left], [right]) => left - right);
  const gaps = [];
  let cursor = 0;
  ranges.forEach(([start, end]) => {
    if (start > cursor) gaps.push([cursor, start]);
    cursor = Math.max(cursor, end);
  });
  if (cursor < DAY_MINUTES) gaps.push([cursor, DAY_MINUTES]);
  if (gaps.length === 0) return { startTime: "", endTime: "" };

  const preferredStart = toMinutes(periods[periods.length - 1].endTime);
  const gap =
    gaps.find(([start]) => start === preferredStart) || gaps[0];
  const [start, gapEnd] = gap;
  const end = Math.min(start + 60, gapEnd);
  const format = (minutes) =>
    `${String(Math.floor((minutes % DAY_MINUTES) / 60)).padStart(2, "0")}:${String(
      minutes % 60,
    ).padStart(2, "0")}`;
  return { startTime: format(start), endTime: format(end) };
};

export const isScheduleDayFull = (daySchedule) => {
  const nextPeriod = getNextSchedulePeriod(daySchedule);
  return !nextPeriod.startTime && !nextPeriod.endTime;
};

const formatTime = (time) => {
  const minutes = toMinutes(time);
  if (minutes === null) return time || "--";
  const hours = Math.floor(minutes / 60);
  const suffix = hours >= 12 ? "PM" : "AM";
  return `${String(hours % 12 || 12).padStart(2, "0")}:${String(
    minutes % 60,
  ).padStart(2, "0")} ${suffix}`;
};

export const validateSchedulePeriods = (schedule) => {
  const selectedDays = schedule?.days || [];
  const dayTimes = schedule?.dayTimes || {};
  const errors = [];

  if (selectedDays.length === 0) {
    return [{ day: null, index: null, message: "Select at least one day." }];
  }

  selectedDays.forEach((day) => {
    const periods = getSchedulePeriods(dayTimes[day]);
    periods.forEach((period, index) => {
      const start = toMinutes(period.startTime);
      const end = toMinutes(period.endTime);
      let message = "";

      if (start === null || end === null) {
        message = "Enter both a start time and an end time.";
      } else if (end === start) {
        message = "Start time and end time must be different.";
      } else {
        const ranges = getPeriodRanges(period);
        const overlaps = periods.flatMap((other, otherIndex) => {
          if (otherIndex === index) return [];
          const otherRanges = getPeriodRanges(other);
          const overlapsRange = ranges.some(([rangeStart, rangeEnd]) =>
            otherRanges.some(
              ([otherStart, otherEnd]) =>
                rangeStart < otherEnd && otherStart < rangeEnd,
            ),
          );
          if (!overlapsRange) {
            return [];
          }

          return [
            `Time Period ${otherIndex + 1} (${formatTime(
              other.startTime,
            )} - ${formatTime(other.endTime)})`,
          ];
        });

        if (overlaps.length > 0) {
          message = `Overlaps with ${overlaps.join(
            ", ",
          )}. Change the times so the periods don’t overlap.`;
        }
      }

      if (message) errors.push({ day, index, message });
    });
  });

  return errors;
};
